/**
 * Copyright © 2026 Adnuntius AS.
 */
import {Component, OnInit, signal} from '@angular/core';
import {NgForm} from '@angular/forms';
import {BsModalRef} from 'ngx-bootstrap/modal';
import * as _ from 'lodash';

import template from './line-item-cpm-fees-modal.component.html';
import {ADVERTISING_SHARED} from '../advertising/advertising-shared';
import {MoneyHelper} from '../../components/util/money';
import {deferSignalStateWrite} from '../common/util/template-state';
import {calculateNetCpm} from './fees-calculator';
import {LineItemCpmFeesModalResult} from './line-item-cpm-fees-modal.service';

@Component({
  standalone: true,
  imports: [...ADVERTISING_SHARED],
  selector: 'adn-line-item-cpm-fees-modal',
  template,
  styles: [`
    .list-group-item {
      border: 1px solid var(--adn-gl-border-default, #ddd);
      border-radius: 4px;
      margin-bottom: 10px;
    }

    .list-group-item + .list-group-item {
      border-top: 1px solid var(--adn-gl-border-default, #ddd);
    }

    .list-group-item:hover,
    .list-group-item:focus {
      background-color: transparent;
    }

    .pull-right .btn + .btn {
      margin-left: 8px;
    }
  `]
})
export class LineItemCpmFeesModalComponent implements OnInit {
  moneyCurrency: any;
  uiFees: any = {fees: [], grossCpm: null, netCpm: null};
  isAdOps = false;
  isPureMarketplace = false;
  isAuction = true;
  isNew = false;
  readOnly = false;
  onSave: (uiFees: any) => Promise<any>;
  onComplete: (result: LineItemCpmFeesModalResult) => void;

  readonly err = signal<any[]>([]);
  feesTooHigh = false;
  readonly disabled = signal(false);

  private readonly feeFormKeys = new WeakMap<object, number>();
  private nextFeeFormKey = 0;

  constructor(public bsModalRef: BsModalRef) {
  }

  ngOnInit(): void {
    this.uiFees = this.uiFees || {fees: [], grossCpm: null, netCpm: null};
    this.uiFees.fees = this.uiFees.fees || [];
    this.adjustCpm();
  }

  dismiss(): void {
    this.bsModalRef.hide();
  }

  adjustCpm(): void {
    if (!MoneyHelper.isMoney(_.get(this.uiFees, ['grossCpm']))) {
      return;
    }
    this.uiFees.netCpm = calculateNetCpm(this.uiFees.fees, this.uiFees.grossCpm);
    this.feesTooHigh = _.get(this.uiFees.netCpm, 'amount') < 0;
  }

  addCpmFee(): void {
    this.uiFees.fees = this.uiFees.fees || [];
    this.uiFees.fees.unshift({type: 'MONETARY'});
    this.adjustCpm();
  }

  addSupplementaryFee(): void {
    this.uiFees.fees = this.uiFees.fees || [];
    this.uiFees.fees.unshift({
      id: 'supplementary-fee',
      type: 'MONETARY'
    });
    this.adjustCpm();
  }

  removeCpmFee(cpmFee: any): void {
    const index = (this.uiFees.fees || []).indexOf(cpmFee);
    if (index !== -1) {
      this.uiFees.fees.splice(index, 1);
      this.adjustCpm();
    }
  }

  isFeeNameReadOnly(cpmFee: any): boolean {
    return cpmFee.id === 'tech-fee'
      || cpmFee.id === 'audience-fee'
      || cpmFee.id === 'creative-format-fee'
      || (cpmFee.id === 'supplementary-fee' && !this.isAdOps);
  }

  isFeeEditable(cpmFee: any): boolean {
    return !this.readOnly && !this.isFeeNameReadOnly(cpmFee);
  }

  feeControlName(cpmFee: any, field: string): string {
    if (!this.feeFormKeys.has(cpmFee)) {
      this.feeFormKeys.set(cpmFee, this.nextFeeFormKey++);
    }
    return `cpmFee${this.feeFormKeys.get(cpmFee)}.${field}`;
  }

  hasUnnamedFees(): boolean {
    return _.some(this.uiFees.fees || [], (fee) => {
      return !this.isFeeNameReadOnly(fee) && !_.trim(fee.name || '');
    });
  }

  saveCpmFees(form?: NgForm): void {
    if (form?.invalid || this.hasUnnamedFees()) {
      form?.form.markAllAsTouched();
      return;
    }
    if (this.feesTooHigh || !this.onSave) {
      return;
    }
    this.err.set([]);
    this.disabled.set(true);
    this.onSave(_.cloneDeep(this.uiFees)).then(() => {
      deferSignalStateWrite(() => {
        this.onComplete?.({saved: true, uiFees: _.cloneDeep(this.uiFees)});
        this.bsModalRef.hide();
      });
    }, (err: any) => {
      deferSignalStateWrite(() => {
        this.err.set(_.get(err, ['data', 'errors'], []));
        this.disabled.set(false);
      });
    });
  }
}
